/**
 * Package B (docs/recipe-import-plan): a pasted recipe, as the iPhone's Scan
 * Text or Live Text hands it over, into grocery rows. Rules only — no
 * model, no network — and dependency-free apart from `parseQty`, which is
 * the one grammar a quantity has in this app; this file only cleans the
 * line before it and the body after it.
 *
 * What a pasted page looks like: a title, maybe "Serves 4", a heading like
 * "Ingredients", the lines, sometimes a sub-heading ("For the sauce:"),
 * then "Method" or "Directions" and prose. Scan Text also breaks a long
 * ingredient across two lines now and then (`2 cups all-purpose` / `flour`).
 * Each of those is a rule below; the fixtures in `test/recipe-lines.test.ts`
 * are real pastes.
 */
import { itemKey, parseQty } from './listParse.ts'

export interface RecipeRow {
  body: string
  qty: string | null
  /** The sub-heading the line sat under, as printed, or null. */
  section: string | null
  /** Salt, pepper, water, sugar, flour, oil, vinegar — the brief's fixed
   *  list. Unticked by default: Doug has them. */
  staple: boolean
}

/** Once one of these appears on its own line, everything after it is the
 *  method, not the shopping. */
const METHOD_HEAD = /^(?:directions?|method|instructions?|preparation|steps|procedure|zubereitung|anleitung|so geht'?s)\b/i
/** Lines that are the page's furniture, not an ingredient. */
const FURNITURE = /^(?:ingredients?|zutaten|serves|servings?|yield|makes|prep(?:aration)? time|cook(?:ing)? time|total time|ready in|portionen|für \d+ personen|personen|dauer|schwierigkeit|calories|kcal|notes?|tipp|tips?|equipment)\b/i
/** An instruction, by its first word. */
const IMPERATIVE = /^(?:preheat|heat|stir|mix|add|bake|combine|whisk|cook|place|pour|bring|season|serve|remove|let|cut|slice|chop|melt|simmer|boil|fry|saut[ée]|roast|grill|transfer|drain|set|cover|fold|beat|sprinkle|garnish|toss|spread|arrange|rinse|wash|peel|dice|mince|crush|knead|roll|shape|rest|chill|refrigerate|freeze|reduce|return|repeat|divide|top|fill|layer|blend|puree|purée|process|meanwhile|then|once|when|while|in a|in the|using|put|make|take|turn|lower|raise|allow|continue|finish|den|die|das|zuerst|dann|danach|anschließend|inzwischen|nun|jetzt|alles|den backofen|die zwiebeln?|mit|in einer|in einem|bei)\b/i

/** Words after the amount that say how to prepare the thing, not what to
 *  buy. Removed as whole words wherever they stand. */
const PREP = new RegExp(String.raw`\b(?:finely|roughly|coarsely|thinly|thickly|freshly|lightly|very|about|approx\.?|approximately|chopped|diced|minced|sliced|grated|shredded|crushed|peeled|cored|seeded|deseeded|pitted|halved|quartered|cubed|julienned|melted|softened|beaten|lightly beaten|whisked|sifted|toasted|rinsed|drained|divided|optional|to taste|as needed|if needed|plus more|plus extra|for serving|for garnish|for the pan|for frying|for brushing|for dusting|at room temperature|room temperature|cold|warm|hot|lukewarm|crumbled|large|small|medium|big|fresh|ripe|groß|große|großen|klein|kleine|kleinen|reif|reife|gehackt|gewürfelt|geschnitten|gerieben|geschält|zerdrückt|geschmolzen|gehobelt|gepresst|nach belieben|nach geschmack|zum servieren|zum braten|zum garnieren|zum bestäuben|optional|fein|grob|frisch|etwas|evtl\.?|eventuell)\b\.?`, 'gi')

/** A single word that ends a line because Scan Text broke the ingredient
 *  there, not because the ingredient did. */
const DANGLING = /(?:-|\b(?:all-purpose|plain|fresh|ground|large|small|medium|dried|red|green|yellow|white|black|brown|extra-virgin|extra virgin|virgin|olive|unsalted|salted|granulated|powdered|heavy|sour|whole|baking|vanilla|chicken|beef|vegetable|sea|kosher|light|dark|sweet|hot|cold|of|and|or|with))$/i

const BULLET = /^(?:[•\-–—*·▪◦☐✓✔o]|\d+[.)](?=\s)|\(\d+\))\s*/

const FRACTIONS: [RegExp, string][] = [
  [/\b1\/2\b/g, '½'], [/\b1\/4\b/g, '¼'], [/\b3\/4\b/g, '¾'], [/\b1\/3\b/g, '⅓'], [/\b2\/3\b/g, '⅔'], [/\b1\/8\b/g, '⅛'],
]

/** Exact bodies, lowercased, that are a staple. Variants a recipe prints. */
const STAPLES = new Set([
  'salt', 'sea salt', 'kosher salt', 'table salt', 'flaky salt', 'pepper', 'black pepper', 'white pepper',
  'ground black pepper', 'salt and pepper', 'salt & pepper', 'water', 'warm water', 'cold water', 'hot water',
  'sugar', 'white sugar', 'granulated sugar', 'caster sugar', 'flour', 'all-purpose flour', 'all purpose flour',
  'plain flour', 'oil', 'olive oil', 'extra-virgin olive oil', 'extra virgin olive oil', 'vegetable oil',
  'cooking oil', 'canola oil', 'sunflower oil', 'neutral oil', 'vinegar', 'white vinegar',
  'salz', 'meersalz', 'pfeffer', 'schwarzer pfeffer', 'salz und pfeffer', 'wasser', 'zucker', 'mehl',
  'weizenmehl', 'öl', 'olivenöl', 'pflanzenöl', 'sonnenblumenöl', 'rapsöl', 'essig', 'weißweinessig',
])

export const isStaple = (body: string): boolean => STAPLES.has(body.trim().toLowerCase())

/** `2-3 cloves` and `2 to 3`: the larger number, since this is a shopping
 *  list and the smaller one runs out. */
const RANGE = /\b(\d+)\s*(?:-|–|to|bis)\s*(\d+)\b/

function joinBrokenLines(lines: string[]): string[] {
  const out: string[] = []
  for (const line of lines) {
    const prev = out[out.length - 1]
    const oneWord = /^[a-zäöüß][\wäöüß-]*$/i.test(line)
    if (prev && oneWord && DANGLING.test(prev) && !/[:.]$/.test(prev)) {
      out[out.length - 1] = `${prev}${prev.endsWith('-') ? '' : ' '}${line}`
    } else {
      out.push(line)
    }
  }
  return out
}

/** One cleaned line into a body and a quantity, or null for nothing to buy. */
export function parseRecipeLine(raw: string): { body: string; qty: string | null } | null {
  let s = raw.replace(BULLET, '').trim()
  for (const [re, glyph] of FRACTIONS) s = s.replace(re, glyph)
  s = s.replace(/(\d)\s+([½¼¾⅓⅔⅛])/g, '$1$2')
  s = s.replace(RANGE, (_, a: string, b: string) => String(Math.max(Number(a), Number(b))))
  s = s.replace(/\([^)]*\)/g, ' ')
  // The first clause is the item; what follows a comma, a dash, "or", "for",
  // "plus" is preparation, an alternative, or a purpose.
  s = s.split(/,|;|\s[–—-]\s|\s+(?:or|oder|for|plus)\s+/i)[0] ?? ''
  s = s.replace(PREP, ' ').replace(/\s+/g, ' ').replace(/^[\s,.:;-]+|[\s,.:;-]+$/g, '')
  if (!s) return null
  // "a pinch of", "a handful of", "some": a quantity in words, which the
  // list does not keep — 1 is never shown, and neither is "some".
  s = s.replace(/^(?:a pinch of|a handful of|a few|a little|some|a|an|ein paar|ein wenig|etwas|einige|ein|eine)\s+/i, '')
  const { body, qty } = parseQty(s)
  let cleaned = body.replace(/^(?:of)\s+/i, '').trim()
  if (!cleaned || /^[\d½¼¾⅓⅔⅛.,\s]+$/.test(cleaned)) return null
  // `1 can whole tomatoes`: the can is the shelf. The amount keeps its unit
  // and the body says what to buy — canned tomatoes, not tomatoes — which is
  // also what the aisle matcher's form rule reads.
  const m = qty?.match(/^(\d+)\s*(cans?|tins?|jars?|dosen?|glas)$/i)
  if (m && !/^(?:canned|tinned|jarred|dosen|eingelegt)/i.test(cleaned)) {
    const unit = m[2]!.toLowerCase()
    const word = /^(dose|dosen|glas)$/.test(unit) ? 'Dosen-' : unit.startsWith('jar') ? 'jarred ' : 'canned '
    cleaned = `${word}${cleaned}`
    // The can is now in the body; the amount is a count of cans.
    return { body: cleaned, qty: Number(m[1]) > 1 ? `×${m[1]}` : null }
  }
  return { body: cleaned, qty: qty === '×1' ? null : qty }
}

export function parseRecipe(text: string): RecipeRow[] {
  const lines = joinBrokenLines(text.split(/\r?\n/).map(l => l.trim()).filter(Boolean))
  const rows: RecipeRow[] = []
  const seen = new Set<string>()
  let section: string | null = null
  // The first line is the recipe's name when it carries no amount and the
  // line after it does, or is furniture ("Serves 4"). A list that opens
  // with a bare "salt" and then "2 eggs" loses the salt to this rule; a
  // staple, and the lesser wrong.
  const first = lines[0]
  const second = lines[1]
  const start = first && second && !/\d|[½¼¾⅓⅔⅛]/.test(first) && !/:$/.test(first)
    && (/^[\d½¼¾⅓⅔⅛•\-–*]/.test(second) || FURNITURE.test(second)) ? 1 : 0
  for (const line of lines.slice(start)) {
    if (METHOD_HEAD.test(line)) break
    if (FURNITURE.test(line)) { if (/^(?:ingredients?|zutaten)/i.test(line)) section = null; continue }
    const head = line.match(/^(?:for the|für (?:die|den|das)|for)\s+(.+?):?$/i)
    if (head || /:$/.test(line)) {
      section = (head ? head[1] : line.replace(/:$/, '')).trim()
      continue
    }
    if (line.length > 80 || IMPERATIVE.test(line) || /\.\s+[A-ZÄÖÜ]/.test(line)) continue
    // "salt and pepper" is two things; "2 eggs and 1 cup milk" is a sentence,
    // left alone — the split only applies when no amount leads the line.
    const pieces = /^\d|^[½¼¾⅓⅔⅛]/.test(line) ? [line] : line.split(/\s+(?:and|und|&)\s+/i)
    for (const piece of pieces) {
      const parsed = parseRecipeLine(piece)
      if (!parsed) continue
      const key = itemKey(parsed.body)
      if (seen.has(key)) continue
      seen.add(key)
      rows.push({ ...parsed, section, staple: isStaple(parsed.body) })
    }
  }
  return rows
}

/**
 * Which rows start ticked: everything but a staple and anything already
 * open on the list (`openBodies`, matched the way `planGroceryAdd` matches
 * a re-typed item). Pure, so the sheet's default is one test and not a
 * render.
 */
export function defaultTicks(rows: readonly { body: string; staple: boolean }[], openBodies: readonly string[]): boolean[] {
  const open = new Set(openBodies.map(itemKey))
  return rows.map(r => !r.staple && !open.has(itemKey(r.body)))
}

/**
 * What OCR adds to a page that a person never typed: a line of nothing but
 * punctuation where a rule or a photo edge was, a `|` where the column
 * line ran, `l` and `I` standing in for `1` before a unit, a hyphen that
 * is really a bullet. Removed before `parseRecipe`, which otherwise would
 * read `l cup` as an item called "l cup".
 */
export function cleanOcrText(text: string): string {
  return text
    .split(/\r?\n/)
    .map(l => l
      .replace(/[|¦]+/g, ' ')
      .replace(/^[\s\-–—•·*_=~.:;,'"`´]+(?=\S)/, '')
      .replace(/^[lI](?=\s?(?:\/\d|[½¼¾⅓⅔⅛]|\s+(?:cups?|tbsp|tsp|tablespoons?|teaspoons?|cloves?|lbs?|oz|g|kg|ml|l|EL|TL|Bund|Prise)\b))/, '1')
      .replace(/(?<=\d\s?)[lI](?=\/\d)/g, '1')
      .replace(/\s{2,}/g, ' ')
      .trim())
    .filter(l => /[A-Za-zÄÖÜäöüß]{2}/.test(l))
    .join('\n')
}

/**
 * The Shortcut route (README, *Route 1*): the phone's own OCR hands the text
 * over in the hash, `#/lists?recipe=<url-encoded text>`, which never
 * reaches a server. `clipboard` means the Shortcut copied it instead and the
 * sheet should offer its Paste button; anything else is the text.
 */
export function recipeFromQuery(query: Record<string, string>): { text: string } | { clipboard: true } | null {
  const raw = query.recipe
  if (raw == null) return null
  if (raw === 'clipboard' || raw === '') return { clipboard: true }
  return { text: raw }
}
